import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, UserPlus, LogIn, CheckCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Turnstile } from '../components/Turnstile';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [usePassword, setUsePassword] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<'signup' | 'magic-link' | null>(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const { user, signInWithOtp, signInWithPassword, signUpWithPassword, profile, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[Login] Component mounted or location changed.');
    // Check for error message from auth callback
    const error = location.state?.error;
    if (error) {
      console.log('[Login] Received error state from location:', error);
      setMessage(error);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[Login] handleSubmit triggered.');
    
    if (!turnstileToken) {
      console.log('[Login] Turnstile token missing.');
      setMessage('Please complete the Cloudflare Turnstile challenge');
      return;
    }
    
    setMessage(''); // Clear previous messages
    setLoading(true);
    console.log(`[Login] Attempting action: ${isSignUp ? 'Sign Up' : (usePassword ? 'Sign In (Password)' : 'Sign In (OTP)')}`);

    try {
      if (isSignUp) {
        // Sign up flow
        if (usePassword) {
          if (!password) {
            setMessage('Password is required for sign up');
            setLoading(false);
            return;
          }
          await signUpWithPassword(email.toLowerCase(), password, turnstileToken);
          console.log('[Login] Sign up successful, showing confirmation.');
          setShowConfirmation('signup');
        } else {
          setMessage('Email OTP is only available for sign in. Please use password for sign up.');
        }
      } else {
        // Sign in flow
        if (usePassword) {
          if (!password) {
            setMessage('Password is required');
            setLoading(false);
            return;
          }
          await signInWithPassword(email.toLowerCase(), password, turnstileToken);
          console.log('[Login] Password sign in initiated. Waiting for auth state change and profile...');
        } else {
          await signInWithOtp(email.toLowerCase(), turnstileToken);
          console.log('[Login] OTP sign in initiated, showing confirmation.');
          setShowConfirmation('magic-link');
        }
      }
    } catch (error: any) {
      console.error('[Login] handleSubmit error:', error);
      setMessage(`Error: ${error.message || 'Something went wrong. Please try again.'}`);
      setTurnstileToken('');
      setAttemptCount(prev => prev + 1);
    } finally {
      console.log('[Login] handleSubmit finished.');
      // Only set loading false if not showing confirmation (which replaces the form)
      if (!showConfirmation) {
          setLoading(false);
      }
    }
  };

  const toggleAuthMode = () => {
    console.log('[Login] Toggling auth mode.');
    setIsSignUp(!isSignUp);
    setUsePassword(true); // Default to password for both modes initially
    setMessage('');
    setShowConfirmation(null);
  };

  const toggleAuthMethod = () => {
    console.log('[Login] Toggling auth method.');
    setUsePassword(!usePassword);
    setMessage('');
    setShowConfirmation(null);
  };
  
  // Function to redirect users based on their role or profile completeness
  const redirectBasedOnRole = () => {
    console.log('[Login] redirectBasedOnRole called. Current profile:', profile);
    
    // If profile isn't loaded yet (or user isn't authenticated fully), don't redirect
    // Allow the useEffect to handle waiting for the profile.
    if (!profile || !user) {
        console.log('[Login] redirectBasedOnRole: Profile or user not ready yet.');
        return;
    }
    
    // Check profile completion *after* ensuring profile exists
    if (!profile.first_name || !profile.last_name || !profile.phone_number) {
      console.log('[Login] Profile incomplete, redirecting to /profile');
      navigate('/profile', { replace: true });
      return;
    }
    
    // Redirect based on role
    const targetPath = `/${profile.role}`;
    console.log(`[Login] Profile complete, redirecting to role-based path: ${targetPath}`);
    switch (profile.role) {
      case 'administrator':
        navigate('/administration', { replace: true });
        break;
      case 'coach':
        navigate('/coach', { replace: true });
        break;
      case 'parent':
        navigate('/parent', { replace: true });
        break;
      default:
        console.warn(`[Login] Unknown role or default case hit: ${profile.role}. Redirecting to /profile.`);
        navigate('/profile', { replace: true }); // Fallback to profile or a default dashboard
        break;
    }
  };
  
  // Effect to handle redirection after successful password login when profile is loaded
  useEffect(() => {
    console.log('[Login] useEffect triggered. User:', user?.id, 'Profile:', profile?.id, 'AuthLoading:', authLoading);
    // Only attempt redirection if:
    // 1. User is logged in (user object exists)
    // 2. Profile is loaded (profile object exists)
    // 3. Auth context is not in a loading state
    // 4. We are not currently showing a confirmation message (signup/magic link)
    if (user && profile && !authLoading && !showConfirmation) {
        console.log('[Login] Conditions met for redirection check.');
        setMessage('Signed in successfully! Redirecting...'); // Give feedback
        // Use setTimeout to allow the message to render briefly before navigating
        const timer = setTimeout(() => {
            redirectBasedOnRole();
        }, 100); // Short delay for user feedback
        return () => clearTimeout(timer); // Cleanup timer on unmount or re-run
    } else {
        console.log('[Login] Conditions not met for redirection.');
        // Clear success message if user logs out or profile becomes null while on this page
        if (!user || !profile) {
            // Avoid clearing error messages
            if (message === 'Signed in successfully! Redirecting...') {
                setMessage('');
            }
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile, authLoading, showConfirmation, navigate]); // Add navigate to dependencies

  console.log('[Login] Rendering Login component. State:', { email, loading, message, isSignUp, usePassword, showConfirmation, turnstileToken });

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        {showConfirmation === 'signup' ? (
          // Signup confirmation UI
          <div className="text-center">
            <div className="mx-auto h-16 w-16 text-green-600 flex items-center justify-center">
              <CheckCircle className="h-14 w-14" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">
              Check your email to continue registering
            </h2>
            <div className="mt-4 text-md text-gray-700 space-y-4">
              <p>
                Please check your email for the confirmation link to continue the registration process.
              </p>
              <p>
                You can close this window.
              </p>
            </div>
          </div>
        ) : showConfirmation === 'magic-link' ? (
          // Magic link confirmation UI
          <div className="text-center">
            <div className="mx-auto h-16 w-16 text-green-600 flex items-center justify-center">
              <CheckCircle className="h-14 w-14" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">
              Login link sent!
            </h2>
            <div className="mt-4 text-md text-gray-700 space-y-4">
              <p>
                We've sent a secure login link to <strong>{email}</strong>
              </p>
              <p>
                Please check your email and click on the link to sign in.
              </p>
              <p>
                You can close this browser window.
              </p>
            </div>
          </div>
        ) : (
          // Regular login/signup UI
          <>
            <div className="text-center">
              <div className="mx-auto h-12 w-12 text-indigo-600 flex items-center justify-center">
                {isSignUp ? <UserPlus className="h-8 w-8" /> : <LogIn className="h-8 w-8" />}
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                {isSignUp ? 'Create a new account' : 'Existing account login'}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {isSignUp ? 'Already have an account? ' : 'Don\'t have an account? '}
                {isSignUp && (
                  <button 
                    type="button" 
                    onClick={toggleAuthMode}
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
                  >
                    Sign in
                  </button>
                )}
              </p>
              
              {!isSignUp && (
                <div className="mt-3">
                  <button 
                    type="button" 
                    onClick={toggleAuthMode}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Create an account
                  </button>
                </div>
              )}

              {(!isSignUp && !usePassword) && (
                <div className="mt-3">
                  <button 
                    type="button" 
                    onClick={toggleAuthMethod}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
                  >
                    Use password instead
                  </button>
                </div>
              )}

              {/* Horizontal divider with clear indication for login form */}
              {!isSignUp && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">Login with existing account</span>
                    </div>
                  </div>
                </div>
              )}

            </div>
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      required
                      className="pl-10 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </div>
                </div>
                
                {usePassword && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 flex justify-between items-center">
                      <span>Password</span>
                      {!isSignUp && (
                        <button 
                          type="button" 
                          onClick={toggleAuthMethod}
                          className="ml-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
                        >
                          Get an email link instead
                        </button>
                      )}
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="password"
                        type="password"
                        required={usePassword}
                        className="pl-10 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete={isSignUp ? "new-password" : "current-password"}
                      />
                    </div>
                  </div>
                )}
                
                <div className="mt-4">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onVerify={setTurnstileToken}
                    key={`${isSignUp}-${usePassword}-${attemptCount}`}
                  />
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={loading || authLoading} // Disable button also while auth context is loading
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loading ? 'Processing...' : isSignUp ? 'Sign up' : usePassword ? 'Sign in' : 'Send login link'}
                </button>
              </div>
              {message && (
                <p className={`mt-2 text-center text-sm ${message.startsWith('Error:') ? 'text-red-600' : 'text-green-600'}`}>
                  {message}
                </p>
              )}
              {authLoading && !loading && ( // Show a subtle loading indicator if context is loading but form isn't submitting
                  <p className="mt-2 text-center text-sm text-gray-500">Authenticating...</p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}